import {
  AD_REQUEST_URL,
  METADATA_TYPE_AD_CLIENT,
  METADATA_TYPE_AD_SERVER,
  METADATA_TYPE_AD_VAST,
  METADATA_TYPE_ADSWIZZ_AD,
  METADATA_TYPE_ADSWIZZ_STREAM,
  METADATA_TYPE_ADSWIZZ_PODCAST,
  EVENT_AD_END,
  EVENT_AD_START,
  EVENT_PLAYING,
  EVENT_COMPLETE,
  EVENT_ERROR,
  EVENT_PLAYLIST_ITEM_CHANGE,
  EVENT_STATE,
  EVENT_PAUSE,
  EVENT_AD_SKIP,
  EVENT_DURATION_CHANGE,
  EVENT_POSITION,
  EVENT_TRACKING,
  EVENT_AD_SKIPPABLE,
  EVENT_AD_COMPANION_START,
  EVENT_AD_COMPANION_END,
  EVENT_AD_BREAK_START,
  EVENT_AD_BREAK_END,
  EVENT_MUTE,
  EVENT_UNMUTE,
  EVENT_VOLUME_CHANGE
} from './constants';
import fetch from '../utils/ajax';
import EventEmitter from './event-emitter';
import Player from './player';
import Events from '../utils/events';
import logger from '../utils/logger';
import nextTick from '../utils/nextTick';
import IMA from './advertising/ima';
import TrackingController from './controllers/tracking';
import { round } from '../utils/math';
import formatSeconds from '../utils/format-seconds';
import Timeline from './controllers/timeline';

const STATE_BEFORE_PREROLL = 'BeforePreRoll';
const STATE_CONTENT_PLAYING = 'ContentPlaying';
const STATE_AD_PLAYING = 'AdPlaying';

const defaultOptions = {
  enabled: false,
  timeout: 5000,
  skipOffset: 10,
  rerollTimeout: 8 * 60,

  adContainer: null
};

export default class Advertising extends EventEmitter {
  constructor (api, contentPlayer, options) {
    super();

    this.options = Object.assign({}, defaultOptions, options);
    this.options.serverURL = AD_REQUEST_URL;

    this.api = api;
    this.logger = options.logger || logger;
    this.player = new Player({
      ...contentPlayer._options,
      playbackRate: 1,
      logger: this.logger
    });

    // override the advertisement players tracking.getTrackingContext method
    // this allows us to alter the context so it includes not just the ad context
    // but also the currently playing content context
    this.player.tracking.getTrackingContext = _ => {
      return this.getTrackingContext();
    };

    this.playlist = this.player.playlist;
    this.content = contentPlayer;
    this._computedValues = this.options.computed;

    // add a getter to retrieve the current adToken value
    Object.defineProperty(this._computedValues.context, 'adToken', {
      get: _ => {
        return this.getAdToken();
      }
    });

    Object.defineProperty(this._computedValues.context, 'pofToken', {
      get: _ => {
        return this.options.pofToken;
      }
    });

    this.ads = [];
    this.adBreakAds = [];
    this.currentAd = null;
    this._isSkippable = false;
    this._settings = options.settings;
    this._timeline = options.timeline;
    this._adContainer = options.adContainer;
    this._currentAdToken = null;
    this._lastAdRollTime = options.lastAdRollTime;

    // can't create an IMA instance without an ad container
    if (IMA.isSupported()) {
      this._ima = new IMA(this);
    }

    // The contentTracking object is used to track ad-server ads played on content
    this._contentTracking = new TrackingController(this);

    // Override the contentTracking.getTrackingContext method so that the context
    // includes both ad-server and the currently playing content contexts
    this._contentTracking.getTrackingContext = _ => {
      return this.getTrackingContext();
    };

    if (typeof this._lastAdRollTime === 'undefined') {
      this._setLastAdRollTime(0);
    }

    this.setState(STATE_BEFORE_PREROLL);

    // ensure the event handlers are bound to the correct scope
    this._playerEventHandler = this._playerEventHandler.bind(this);
    this._contentEventHandler = this._contentEventHandler.bind(this);

    // observe events on the advertising and content players
    this.player.observe(this._playerEventHandler);
    this.content.observe(this._contentEventHandler);

    // listen for changes in the timeline
    this._timeline.on(Timeline.EVENT_ACTION, this._timelineHandler, { context: this });
  }

  getAdToken () {
    return this._currentAdToken;
  }

  getAdContainer () {
    return (this.api && this.api.layout && this.api.layout.getAdContainer()) || this._adContainer;
  }

  getAdCustomClickTracker () {
    return this.api && this.api.layout && this.api.layout.getAdCustomClickTracker();
  }

  getTrackingContext () {
    const ad = this.getCurrentAd();
    const source = this.getSource();
    const metadata = (ad && ad.metadata) || {};
    const context = this.content.tracking.getTrackingContext();

    if (ad) {
      context.ad = ad;
    }

    context.contentType = metadata.contentType;
    context.aduid = metadata.uid || source.url;
    context.adsource = source.url;
    context.tags = (ad && ad.tags) || [];

    context.position = this.getPosition();
    context.duration = this.getDuration();

    return context;
  }

  /**
   * Check if currently in an ad break
   *
   * @return {Boolean}
   */
  inAdBreak () {
    return this.state === STATE_AD_PLAYING;
  }

  /**
   * Check if playing an ad through the IMA SDK
   *
   * @return {Boolean}
   */
  inImaAdBreak () {
    return this._ima && this._ima.inAdBreak();
  }

  isLive () {
    return false;
  }

  /**
   * Check if the current ad is skippable
   *
   * @return {Boolean}
   */
  isSkippable () {
    if (this.inImaAdBreak()) {
      return this._ima.isSkippable();
    }
    return this._isSkippable;
  }

  /**
   * Called when a companion ad is started
   *
   * @param  {Object} companion The companion ad that started
   */
  onAdCompanionStart (companion) {
    this.logger.debug('Advertising: ad companion has started');
    this.emit({
      type: EVENT_AD_COMPANION_START,
      companion: companion
    });
  }

  /**
   * Called when a companion ad ends
   *
   * @param  {Object} companion The companion ad that ended
   */
  onAdCompanionEnd (companion) {
    this.logger.debug('Advertising: ad companion has ended');
    this.emit({
      type: EVENT_AD_COMPANION_END,
      companion: companion
    });
  }

  /**
   * Called when an ad break starts
   */
  onAdBreakStart () {
    this.logger.debug('Advertising: ad break has started');
    this._inAdBreak = true;
    this.setState(STATE_AD_PLAYING);
    this.emit(EVENT_AD_BREAK_START);
  }

  /**
   * Called when an ad break ends
   */
  onAdBreakEnd () {
    this.logger.debug('Advertising: ad break has ended');
    this._inAdBreak = false;
    this._isSkippable = false;
    this.setState(STATE_CONTENT_PLAYING);
    this.emit(EVENT_AD_BREAK_END);
  }

  /**
   * Called when an ad starts
   *
   * @param  {Object} ad The ad that started
   */
  onAdStart (ad) {
    this.logger.debug('Advertising: ad has started');

    this._currentAd = ad;

    if (ad.token) {
      this._currentAdToken = ad.token;
    }

    this.emit({
      type: EVENT_AD_START,
      ad: ad
    });

    // trigger adcompanionstart events for all companions on this ad
    if (ad.companions) {
      for (const companion of ad.companions) {
        this.onAdCompanionStart(companion);
      }
    }
  }

  /**
   * Called when an ad ends
   *
   * @param  {Object} ad The ad that ended
   */
  onAdEnd (ad) {
    // trigger adcompanionend events for all companions on this ad
    if (ad.companions) {
      for (const companion of ad.companions) {
        this.onAdCompanionEnd(companion);
      }
    }

    this.emit({
      type: EVENT_AD_END,
      ad: ad
    });

    // empty the current ad if the ad that ended is the one currently being tracked
    if (this._currentAd === ad) {
      this._currentAd = null;
    }
  }

  /**
   * Called when an ad is skipped
   *
   * @param  {Object} ad The ad that was skipped
   */
  onAdSkip (ad) {
    this.emit({
      type: EVENT_AD_SKIP,
      ad: ad
    });
    this.onAdEnd(ad);

    // we assume skipping an ad skips the entire ad break, but this may not always be the case?
    this.onAdBreakEnd();
  }

  /**
   * Called when the current ad becomes skippable
   */
  onAdSkippable () {
    this._isSkippable = true;
    this.emit({
      type: EVENT_AD_SKIPPABLE,
      position: this.getPosition(),
      duration: this.getDuration()
    });
  }

  /**
   * Called whenever the current ads position is updated
   */
  onAdTimeUpdate () {
    this.emit(EVENT_POSITION);
  }

  /**
   * Handle pause events from the content player
   *
   * @param {Object} event
   */
  oncontentpause (event) {}

  /**
   * Handles the duration changes on server-side ads
   *
   * @param {Object} event A player play event
   */
  oncontentdurationchange (event) {
    // lazy-schedule ad breaks when the duration changes for the first time
    // if duration changes further then subsequent changes are ignored, the
    // only issue this causes is the preloading of post roll ad requests
    if (!this._scheduled) {
      this._scheduleAdBreaks();
    }

    if (!this._isPlayingServerSideAd()) return;

    this.emit({
      type: EVENT_DURATION_CHANGE,
      duration: this.currentAd.metadata.duration
    });
  }

  /**
   * Handles position changes on server-side ads
   *
   * @param {Object} event A player play event
   */
  oncontenttimeupdate (event) {
    if (this._isPlayingServerSideAd()) {
      const currentPosition = this.getPosition();
      const currentDuration = this.getDuration();

      this.emit({
        type: EVENT_POSITION,
        position: currentPosition,
        duration: currentDuration
      });

      // complete the current ad when reaching the end of a server-side ad
      if (currentPosition >= currentDuration) {
        this.onadcomplete({
          type: EVENT_COMPLETE,
          position: currentPosition,
          duration: currentDuration
        });
      } else if (
        (!this.isSkippable() && this.getSkipOffset() >= 0) &&
        (currentPosition >= this.getSkipOffset())
      ) {
        this._isSkippable = true;
        this.emit({
          type: EVENT_AD_SKIPPABLE,
          position: currentPosition,
          duration: currentDuration
        });
      }
    }
  }

  oncontentcomplete (event) {
    if (this._isPlayingServerSideAd()) {
      const currentPosition = this.getPosition();
      const currentDuration = this.getDuration();

      this.onadcomplete({
        type: EVENT_COMPLETE,
        position: currentPosition,
        duration: currentDuration
      });
    }
  }

  /**
   * Handles timeupdate events on the adverts player
   *
   * @param {Object} event
   */
  onadtimeupdate (event) {
    this.emit(event);

    // make the ad break skippable once the skip offset is reached
    if (
      (!this.isSkippable() && this.getSkipOffset() >= 0) &&
      (event.position >= this.getSkipOffset())
    ) {
      const currentPosition = this.getPosition();
      const currentDuration = this.getDuration();

      this._isSkippable = true;
      this.emit({
        type: EVENT_AD_SKIPPABLE,
        position: currentPosition,
        duration: currentDuration
      });
    }
  }

  /**
   * Handles an error event from the advertisement player
   *
   * @param {Object} event A player error event
   */
  onaderror (event) {
    this.logger.warn(`Advertising: Error playing advert: ${event.error.message}`);

    if (this.state === STATE_AD_PLAYING && this.adBreakAds.length > 0) {
      this.startAd(this.adBreakAds.shift());
    } else {
      this.endAdBreak();
    }
  }

  /**
   * Handles complete events from the advertisement player
   *
   * @param {Object} event A player complete event
   */
  onadcomplete (event) {
    this.currentAd.complete = true;
    this.emit(event);

    // schedule the call to endAd until after the current event is completely handled
    nextTick(_ => {
      this.endAd(this.currentAd);
    });
  }

  /**
   * Loads a new list of ads from a playlist item
   *
   * @param {Object} event A player playlistselect event
   */
  onplaylistselect (event) {
    this.setState(STATE_BEFORE_PREROLL);

    this.adBreakAds = [];
    this.currentAd = null;

    const playlistItem = this.content.playlist.getPlaylistItem();

    if (playlistItem) {
      // don't load ads if the playlist item is an adswizz type
      if (
        playlistItem.metadata.contentType !== METADATA_TYPE_ADSWIZZ_STREAM &&
        playlistItem.metadata.contentType !== METADATA_TYPE_ADSWIZZ_PODCAST
      ) {
        this.load(playlistItem.ads);
      }
    } else {
      this.clear();
    }
  }

  /**
   * Handles duration changes on advertisements
   *
   * @param {Object} event
   */
  onaddurationchange (event) {
    if (
      (!this.isSkippable() && this.getSkipOffset() >= 0) &&
      (event.duration <= this.getSkipOffset())
    ) {
      const currentPosition = this.getPosition();
      const currentDuration = this.getDuration();
      this._isSkippable = true;
      this.emit({
        type: EVENT_AD_SKIPPABLE,
        position: currentPosition,
        duration: currentDuration
      });
    }
    this.emit(event);
  }

  /**
   * Destroy the analytics instance
   */
  destroy () {
    if (this._ima) {
      this._ima.destroy();
    }
    this.player.unobserve(this._playerEventHandler);
    this.content.unobserve(this._contentEventHandler);
  }

  /**
   * Update the current state
   *
   * @param {string} newState The new state
   */
  setState (newState) {
    if (newState === this.state) return;

    const prevState = this.state;
    this.state = newState;

    this.emit({
      type: EVENT_STATE,
      previousState: prevState,
      currentState: newState
    });
  }

  /**
   * Load a list of advertisements in to the advertising schedule
   *
   * @param {array} ads A list of ads
   */
  load (ads) {
    this.logger.debug(`Advertising: loading ${ads.length} advertisements from playlist item`);

    this.player.unload();

    this.ads = ads;
    this.currentAd = null;
    this.adBreakAds = [];
    this._isSkippable = false;
    this._scheduled = false;

    this._scheduleAdBreaks();
  }

  /**
   * Clear the current context
   */
  unload () {
    this.clear();
  }

  /**
   * Start playback of the current advertisement
   */
  play () {
    if (this.state === STATE_CONTENT_PLAYING || this._isServerSideAd()) {
      return this.content.play();
    }

    const shouldPauseContent = !(this.content.isEnded() || this.content.isPaused());
    if (shouldPauseContent) {
      this.content.pause();
    }

    this.player.play();
  }

  /**
   * Pause playback of the current advertisement
   */
  pause () {
    if (this.state === STATE_CONTENT_PLAYING || this._isServerSideAd()) {
      return this.content.pause();
    }
    this.player.pause();
  }

  /**
   * Stop playback of the current advertisement
   */
  stop () {
    if (this.state === STATE_CONTENT_PLAYING || this._isServerSideAd()) {
      return this.content.stop();
    }
    this.player.stop();
  }

  /**
   * Clear the currently loaded ads
   */
  clear () {
    this.player.stop();
    this.ads = [];
    this.currentAd = null;
    this.adBreakAds = [];
    this._isSkippable = false;
    this.setState(STATE_BEFORE_PREROLL);
  }

  /**
   * Retrieve the current playing advertisement
   *
   * @return {Object}
   */
  getCurrentAd () {
    if (!this.inAdBreak()) return;
    if (this.inImaAdBreak()) return this._ima.getCurrentPlayerAd();

    return this.currentAd;
  }

  /**
   * Retrieve the ads scheduled to be played at the given position
   *
   * @param {number} position The playback position
   *
   * @return {Object[]}
   */
  getScheduledAds (position) {
    if (!this.options.enabled) {
      return [];
    }
    return this._findAdsByPosition(position)
      .filter(ad => {
        // ignore VAST ads, they are scheduled via the IMA SDK
        if (ad.metadata.contentType === METADATA_TYPE_AD_VAST) {
          return false;
        }

        if (
          !this._hasRerollExpired(ad)
          // (ad.metadata.contentType === METADATA_TYPE_AD_CLIENT && ad.played && Date.now() - ad.played < this.rerollTimeout) ||
          // (ad.metadata.contentType === METADATA_TYPE_AD_CLIENT && !this._hasRerollExpired())
        ) {
          this.logger.debug('Advertising: filtered out ad from schedule, reroll timeout has not expired');
          return false;
        }
        return true;
      });
  }

  /**
   * Retrieve an advertisement from the current playlist item by it's UID
   *
   * @param {string} uid An advertisement UID
   *
   * @return {Object|false}
   */
  getAdvertisementByUid (uid) {
    for (let i = 0, len = this.ads.length; i < len; i++) {
      if (this.ads[i].metadata.uid === uid) return this.ads[i];
    }
    return false;
  }

  /**
   * Retrieve the remaining skip offset
   *
   * @return {Number}
   */
  getRemainingSkipOffset () {
    const offset = this.getSkipOffset();
    const remainder = (offset > 0) ? offset - (this.getPosition() | 0) : 0;

    return Math.max(0, Math.min(offset, remainder));
  }

  /**
   * Retrieve the current skip offset
   *
   * @return {Number}
   */
  getSkipOffset () {
    if (this.inImaAdBreak()) return this._ima.getSkipOffset();
    // ensure skipping is always disabled on in-stream ads for live streams
    if (this._isPlayingServerSideAd() && this.content.isLive()) {
      return -1;
    }

    return this.options.skipOffset;
  }

  /**
   * Returns the url of the ad server, with additional params
   *
   * @param {String} url
   *
   * @return {string}
   */
  getRequestUrl (ad) {
    return this._computedValues.replace(this.options.serverURL, encodeURIComponent, {
      ad: ad
    });
  }

  /**
   * Begins playing advertising pre-rolls on the current content
   *
   * @return {boolean} Returns true if there are pre-rolls to play, otherwise false
   */
  playPreroll () {
    if (this.state === STATE_BEFORE_PREROLL) {
      // try start a preroll using the IMA SDK
      if (this._ima) {
        return this._playImaPreroll();
      }

      // try starting a preroll using a custom schedule
      return this._playIonoPreroll();
    }

    // not in a preroll state, continue as normal
    this.setState(STATE_CONTENT_PLAYING);
    if (!this.content.isEnded() && this.content.isPaused()) {
      this.content.play();
    }

    return Promise.resolve(false);
  }

  /**
   * Load and play an IMA preroll ad break
   */
  _playImaPreroll () {
    const imaPromise = this._ima.startPreroll();
    return imaPromise.then(hasPreroll => {
      if (hasPreroll) {
        // disable ad tracking while playing ads through the IMA SDK
        this._contentTracking.setTracker(null);
      } else {
        return this._playIonoPreroll();
      }
      return hasPreroll;
    });
  }

  /**
   * Load and play a custom preroll ad break
   */
  _playIonoPreroll () {
    // try starting a preroll using a custom schedule
    const playlist = this.getScheduledAds(0);
    if (playlist.length > 0) {
      return this.startAdBreak(playlist);
    }
    this.setState(STATE_CONTENT_PLAYING);
    return Promise.resolve(false);
  }

  /**
   * Attempt to start a postroll ad break
   *
   * Returns a Promise that always resolves with a boolean indicating the existence of postroll ads
   *
   * @return {Promise}
   */
  startPostroll () {
    if (this.state === STATE_CONTENT_PLAYING) {
      if (this._ima) {
        return this._startImaPostroll();
      }
      return this._startIonoPostroll();
    }
    return Promise.resolve(false);
  }

  /**
   * Load and play an IMA postroll ad break
   */
  _startImaPostroll () {
    const imaPromise = this._ima.startPostroll();
    return imaPromise.then(hasPostroll => {
      if (hasPostroll) {
        // disable ad tracking while playing ads through the IMA SDK
        this._contentTracking.setTracker(null);
      } else {
        return this._startIonoPostroll();
      }
      return hasPostroll;
    });
  }

  /**
   * Attempt to start an Iono postroll
   *
   * @return {Promise}
   */
  _startIonoPostroll () {
    // try starting a postroll using a custom schedule
    const playlist = this.getScheduledAds(-1);
    if (playlist.length > 0) {
      return this.startAdBreak(playlist);
    }

    return Promise.resolve(false);
  }

  /**
   * Start an advertising break
   *
   * @param {Object[]} playlist List of ads to play during this ad break
   */
  startAdBreak (playlist) {
    if (this.state === STATE_AD_PLAYING) return;

    this.adBreakAds = [];
    this.currentAd = null;
    this._isSkippable = this.getSkipOffset() === 0;

    if (playlist.length <= 0) {
      return Promise.resolve(false);
    }

    // Load all advertisements scheduled for this ad break.
    // NOTE: currently if any of the ad requests fail, the ad break will NOT start
    return this._loadAdvertisements(playlist)
      .then(adBreakPlaylist => {
        if (adBreakPlaylist.length > 0) {
          this.adBreakAds = adBreakPlaylist;

          this.logger.debug('Advertising: Starting ad break');

          this.onAdBreakStart();
          this.startAd(this.adBreakAds.shift());

          return true;
        } else {
          this.setState(STATE_CONTENT_PLAYING);
          if (!this.content.isEnded() && this.content.isPaused()) {
            this.content.play();
          }

          return false;
        }
      }, errors => {
        this.logger.warn('Advertising: Failed to start ad break, there were one or more ads that failed to load');
        this.setState(STATE_CONTENT_PLAYING);
        if (!this.content.isEnded() && this.content.isPaused()) {
          this.content.play();
        }

        return false;
      });
  }

  /**
   * Load and start playback of an advertisement
   *
   * @todo ad response should be merged with original advertisement object to update it
   *
   * @param {Object} ad An advertisement
   */
  startAd (ad) {
    // skip this ad if it's a client-side ad and we've played a client-side ad in the past reroll timeout
    if (!this._hasRerollExpired(ad)) {
      this.logger.debug('Advertising: Skipped ad, the reroll timeout has not expired yet');
      return;
    }

    if (!this.inAdBreak()) {
      return this.startAdBreak([ad]);
    }

    this.currentAd = ad;

    this._loadAdvertisement(ad)
      .then(ad => {
        if (ad) {
          this._startAd(ad);
          return ad;
        }
        return this.nextAd();
      })
      .catch(err => {
        this.logger.debug(`Advertising: Failed to load advertisement: ${err.message}`);
        return this.nextAd();
      });
  }

  /**
   * End the current ad break
   */
  endAdBreak () {
    if (this.state !== STATE_AD_PLAYING) return;

    this.adBreakAds = [];
    this.player.unload();

    this.onAdBreakEnd();

    // prevent resume if this was a postroll
    if (!this.content.isEnded() && this.content.isPaused()) {
      this.content.play();
    }
  }

  /**
   * Ends playback of the current advertisement and moves on to the next ad in the ad break
   */
  endAd () {
    if (this.state !== STATE_AD_PLAYING) return;

    const ad = this.getCurrentAd();

    this.onAdEnd(ad);

    // @todo only attempt to play the next ad in the ad break when we're still in an ad break
    const nextAd = this.adBreakAds.shift();

    // play the next ad in the the ad break
    if (nextAd) {
      this.startAd(nextAd);
    }
    // clear the server-side ad tracker and end the ad break
    else if (this._isServerSideAd(ad)) {
      this._contentTracking.setTracker(null);
      this.endAdBreak();
    }
    // end the ad break
    else {
      this.endAdBreak();
    }
  }

  /**
   * Move to the next advertisement in the advertisements list for the current ad break
   */
  nextAd () {
    const ad = this.adBreakAds.shift();

    // end the current ad break if there isn't another ad in the current ad break
    if (!ad) {
      return this.endAdBreak();
    }

    this.startAd(ad);
  }

  /**
   * Skip the current ad break
   *
   * @todo when skipping, get the total duration of all server-side ads during the ad break and use the result as the position in the content to seek to
   */
  skip () {
    if (this.state !== STATE_AD_PLAYING) return;
    if (this.inImaAdBreak()) return this._ima.skip();
    if (!this.isSkippable()) return;

    const currentAd = this.currentAd;
    const metadata = currentAd.metadata;
    const contentType = metadata.contentType;
    const currentPosition = this.getPosition();
    const currentDuration = this.getDuration();

    this.currentAd.skipped = true;

    // trigger the adskip event on the content player for server-side ads, otherwise trigger it on the advertisement player
    if (currentAd.metadata.contentType === METADATA_TYPE_AD_SERVER) {
      this.content.emit({
        type: EVENT_AD_SKIP,
        ad: currentAd,
        position: currentPosition,
        duration: currentDuration
      });
    } else {
      this.player.emit({
        type: EVENT_AD_SKIP,
        ad: currentAd,
        position: currentPosition,
        duration: currentDuration
      });
    }

    // end the ad break on the next tick, this is to fix an issue where the tracking module
    // would track the skip agains the content instead of the advertisement metadata
    nextTick(_ => {
      // end the current ad break
      this.endAdBreak();

      // seek to the end of server-side ads in the current ad break
      if (currentAd.metadata.contentType === METADATA_TYPE_AD_SERVER) {
        this.content.setPosition(currentAd.position + currentAd.metadata.duration);
      }
    });
  }

  /**
   * Retrieve the duration of the current advertisement
   *
   * @return {Number|false}
   */
  getDuration () {
    if (this.inImaAdBreak()) return this._ima.getDuration();
    if (!this.currentAd) return false;
    if (this._isServerSideAd()) return round(this.currentAd.metadata.duration, 2);
    return this.player.getDuration();
  }

  /**
   * Retrieve the position of the current advertisement
   *
   * @return {Number|false}
   */
  getPosition () {
    if (this.inImaAdBreak()) return this._ima.getPosition();
    if (!this.currentAd) return false;
    if (this._isServerSideAd()) {
      return round(this.content.getPosition() - this.currentAd.position, 2);
    }
    return this.player.getPosition();
  }

  /**
   * Retrieve the playback source of the current advertisement
   *
   * @return {Object|false}
   */
  getSource () {
    if (this.inImaAdBreak()) return this._ima.getSource();
    if (!this.currentAd) return false;
    if (this._isPlayingServerSideAd()) return this.content.getSource();
    return this.player.getSource();
  }

  /**
   * Sort ad break advertisements so that VAST and client-side ads are played before any server-side ads
   *
   * @param  {Object} a
   * @param  {Object} b
   *
   * @return {Number}
   */
  _adBreakSorter (a, b) {
    if (a.metadata.contentType === METADATA_TYPE_AD_SERVER && b.metadata.contentType !== METADATA_TYPE_AD_SERVER) {
      return 1;
    } else if (b.metadata.contentType === METADATA_TYPE_AD_SERVER && a.metadata.contentType !== METADATA_TYPE_AD_SERVER) {
      return -1;
    } else {
      return 0;
    }
  }

  /**
   * Handles playback events from the content player
   *
   * @param {Object} event Playback event
   */
  _contentEventHandler (event) {
    switch (event.type) {
      case EVENT_TRACKING:
      case EVENT_PLAYING: return;
      case EVENT_POSITION: return this.oncontenttimeupdate(event);
      case EVENT_DURATION_CHANGE: return this.oncontentdurationchange(event);
      case EVENT_PLAYLIST_ITEM_CHANGE: return this.onplaylistselect(event);
      case EVENT_PAUSE: this.oncontentpause(event); break;
      case EVENT_COMPLETE: return this.oncontentcomplete(event);
      case EVENT_MUTE:
        this.player.setMute(true);
        break;
      case EVENT_UNMUTE:
        this.player.setMute(false);
        break;
      case EVENT_VOLUME_CHANGE:
        this.player.setVolume(event.volume);
        break;
    }

    // redispatch content events as advert events if the current advertisement is a server-side ad
    if (this._isPlayingServerSideAd()) {
      this.logger.debug(`Advertising: Redispatching ${event.type} event during server-side ad playback`);
      return Events.redispatch(this, event);
    }
  }

  /**
   * Check if we are currently playing a server-side advertisement
   *
   * @return {boolean}
   */
  _isPlayingServerSideAd () {
    return this.state === STATE_AD_PLAYING && this._isServerSideAd();
  }

  /**
   * Check if an advertisement is a server-side advertisement
   *
   * @param {Object} ad Optional advertisement to be checked
   *
   * @return {boolean}
   */
  _isServerSideAd (ad = this.currentAd) {
    if (!ad) {
      return false;
    }
    return [METADATA_TYPE_AD_SERVER, METADATA_TYPE_ADSWIZZ_AD].includes(ad.metadata.contentType);
  }

  /**
   * Handles playback events from the advertising player
   *
   * @param {Object} event Playback event
   */
  _playerEventHandler (event) {
    // prevent handling events when NOT in an ad break
    if (!this.inAdBreak()) return null;

    // end the ad break immediately if there is no current ad playing
    if (!this.currentAd) {
      return this.endAdBreak();
    }

    switch (event.type) {
      case EVENT_POSITION: return this.onadtimeupdate(event);
      case EVENT_COMPLETE: return this.onadcomplete(event);
      case EVENT_ERROR: return this.onaderror(event);
      case EVENT_DURATION_CHANGE: return this.onaddurationchange(event);
      default: return this.emit(event);
    }
  }

  /**
   * Check if the time since last ad reroll exceeds the reroll timeout
   *
   * @return {bool}
   */
  _hasRerollExpired (ad) {
    if (ad.metadata.contentType !== METADATA_TYPE_AD_CLIENT || !ad.played) {
      return true;
    }

    return (Date.now() - ad.played) > (this.options.rerollTimeout * 1000);
  }

  /**
   * Update the las adreroll time
   *
   * @param {int} time
   */
  _setLastAdRollTime (time) {
    this._lastAdRollTime = time;
  }

  /**
   * Start playback of a loaded advertisement
   *
   * @param  {Object} ad
   */
  _startAd (ad) {
    if (this._isServerSideAd(ad)) {
      this.logger.debug(`Advertising: Starting server-side advertisement ${ad.metadata.uid} (${ad.metadata.duration})`);

      // dissallow skipping of server-side ads
      this._isSkippable = false;

      this.emit({
        type: EVENT_PLAYLIST_ITEM_CHANGE,
        playlistIndex: 0,
        playlistItem: ad
      });

      // set the current server-side ad tracker to the chosen ad
      const tracker = this._contentTracking.createTracker(ad);
      this._contentTracking.setTracker(tracker);

      // resume playback if needed
      if (this.content.isPaused()) {
        this.content.play();
      }
    } else {
      this.logger.debug(`Advertising: Starting ${ad.metadata.contentType} advertisement ${ad.metadata.uid}`);

      this.player.load(ad);
      this.play();

      // update last adreroll time
      this._setLastAdRollTime(Date.now());
    }

    ad.played = new Date();
    this.currentAd = ad;

    if (ad.token) {
      this._currentAdToken = ad.token;
    }

    this.emit(EVENT_AD_START);

    if (ad.companions) {
      for (const companion of ad.companions) {
        this.emit({
          type: EVENT_AD_COMPANION_START,
          companion: companion
        });
      }
    }
  }

  _timelineHandler (event) {
    const entry = event.entry || this._timeline.current();

    if (event.type === Timeline.EVENT_ACTION && entry.action === Timeline.ACTION_AD_REQUEST) {
      this.logger.debug('Advertising: Requesting an ad from the ad server');
      this._loadAdvertisement(entry.ad)
        .then(ad => {
          if (ad) {
            this._scheduleAd(ad);
          }
        })
        .catch(err => {
          this.logger.warn(`Advertising: Failed to request an ad from the ad server - ${err.message}`);
        });
    } else if (event.type === Timeline.EVENT_ACTION && entry.action === Timeline.ACTION_AD_START) {
      this.logger.debug(`Advertising: Timeline indicates an ad has started at ${formatSeconds(entry.startTime)}`);
      this.startAd(entry.ad);
    }
  }

  /**
   * Parse a client-side ad server response
   *
   * @param  {Object} response
   *
   * @return {Promise}
   */
  _parseClientAdResponse (response) {
    const self = this;

    const contentLength = parseInt(response.headers.get('content-length'), 10);
    if (response.status === 204 || contentLength === 0) {
      self.logger.debug('Advertising: ad response does not contain any ads');
      return Promise.resolve(null);
    }

    if (response.status >= 400) {
      self.logger.debug(`Advertising: ad response returned an error code [${response.status}]`);
      return Promise.resolve(null);
    }

    return response.json()
      .then(json => {
        self.logger.debug('Advertising: Parsing client ad response');

        // handle playlist responses
        const contentType = json.metadata && json.metadata.contentType;
        if (contentType === 'playlist') {
          // derive the ad token from the playlist data
          this._currentAdToken = json.token || null;
          // return the first ad item in the playlist
          return json.items.length > 0 ? json.items[0] : null;
        }

        return json;
      });
  }

  /**
   * Request and parse a client-side ad response
   *
   * @param  {Object} ad
   *
   * @return {Promise}
   */
  _loadClientAdvertisement (ad) {
    if (!this._hasRerollExpired(ad)) {
      this.logger.debug('Advertising: Skipping loading of advertisement, the reroll timeout has not expired');
      return Promise.resolve(null);
    }

    // return ad as-is when it already contains audio
    if (ad.audio.length > 0) {
      return Promise.resolve(ad);
    }

    // compute the request url
    const requestUrl = this.getRequestUrl(ad);

    // 1. request ad from ad server
    return fetch(requestUrl)
      // 2. parse ad server response to an advertisement
      .then(this._parseClientAdResponse.bind(this))
      .then(clientAd => {
        if (clientAd) {
          // forward the original ad position
          clientAd.position = ad.position;
          clientAd.slot = ad.slot;

          // proxy properties on the original ad object
          Object.defineProperties(clientAd, {
            skipped: {
              get () { return ad.skipped; },
              set (val) { ad.skipped = val; }
            },
            interrupted: {
              get () { return ad.interrupted; },
              set (val) { ad.interrupted = val; }
            },
            complete: {
              get () { return ad.complete; },
              set (val) { ad.complete = val; }
            },
            loaded: {
              get () { return ad.loaded; },
              set (val) { ad.loaded = val; }
            },
            played: {
              get () { return ad.played; },
              set (val) { ad.played = val; }
            }
          });
        }
        return clientAd;
      })
      .catch(err => {
        this.logger.debug('Advertising: ad server returned an error status');
        return Promise.resolve(null);
      });
  }

  /**
   * Load a server-side advertisement
   *
   * @param  {Object} ad
   *
   * @return {Promise}
   */
  _loadServerAdvertisement (ad) {
    return Promise.resolve(ad);
  }

  /**
   * Load and parse an advertisement
   *
   * @param  {Object} ad
   *
   * @return {Promise}
   */
  _loadAdvertisement (ad) {
    this.logger.debug('Advertisement: Loading advertisement');

    switch (ad.metadata.contentType) {
      case METADATA_TYPE_AD_SERVER:
      case METADATA_TYPE_ADSWIZZ_AD:
        return this._loadServerAdvertisement(ad);
      case METADATA_TYPE_AD_CLIENT:
        return this._loadClientAdvertisement(ad);
      default:
        throw new Error("Unrecognized ad content type '" + ad.metadata.contentType + "'");
    }
  }

  /**
   * Load and parse a list of advertisements
   *
   * @param  {Object} ads
   *
   * @return {Promise}
   */
  _loadAdvertisements (ads) {
    const adLoadPromises = ads.map((ad) => {
      return this._loadAdvertisement(ad);
    });

    const mergeAdvertisements = function (advertisements) {
      return Array.from(advertisements).reduce(function (playlist, ad) {
        // filter out skipped ads
        if (ad !== null) {
          return playlist.concat(ad);
        }
        return playlist;
      }, []);
    };

    const sortAdvertisements = (advertisements) => {
      return advertisements.sort(this._adBreakSorter);
    };

    return Promise
      .all(adLoadPromises)
      .then(mergeAdvertisements)
      .then(sortAdvertisements);
  }

  /**
   * Find advertisements in the list satisfied by a position
   *
   * @param  {Array}  ads
   * @param  {Number} position
   *
   * @return {Array}
   */
  _findAdsByPosition (position) {
    return this.ads.filter(function (ad) {
      // check preroll
      if (position === 0 && ad.position === 0) {
        return true;
      }
      // check postroll
      if (position === -1 && ad.position === -1) {
        return true;
      }
      // check midroll
      return Math.abs(ad.position - position) <= 0.5;
    });
  }

  /**
   * Add an advertisement to the advertising schedule
   *
   * @param {object} ad An ad playlist item
   */
  _scheduleAd (ad) {
    // don't schedule pre-rolls
    if (ad.position === 0 || ad.slot === 'pre') {
      return;
    }

    // don't schedule post-rolls
    if (ad.position === -1 || ad.slot === 'post') {
      return;
    }

    if (ad.metadata.contentType === METADATA_TYPE_AD_CLIENT && ad.audio.length === 0) {
      let adRequestPosition = Math.max(0, ad.position - (this.options.timeout / 1000));

      // update the ad request position to use the content duration for post-rolls
      if (ad.position === -1 || ad.position >= this.content.getDuration()) {
        adRequestPosition = this.content.getDuration() - (this.options.timeout / 1000);
      }

      // insert an ad request action on to the timeline
      this._timeline.insert({
        action: Timeline.ACTION_AD_REQUEST,
        ad: ad,
        startTime: adRequestPosition
      });
    } else if (ad.position > 0 && (ad.position < this.content.getDuration() || this.content.isLive())) {
      this.logger.debug(`Advertising: scheduling an ad to play at ${ad.position}`);
      this._timeline.insert({
        action: Timeline.ACTION_AD_START,
        ad: ad,
        startTime: ad.position
      });
    }
  }

  /**
   * Schedule ad breaks for the ads in the content player playlist item
   *
   * @todo instead of scheduling individual ads, schedule the ad breaks themselves
   *
   * @param {Boolean} includeImaAds Whether or not to schedule IMA ads (we send false when duration of the content changes as the SDK handles this itself)
   */
  _scheduleAdBreaks (includeImaAds = true) {
    if (!this.options.enabled || this._scheduled) {
      return;
    }

    // skip scheduling when no audio duration available
    if (this.content.getDuration() === 0) {
      return;
    }

    this._scheduled = true;

    const groups = this.ads.reduce((group, ad) => {
      const key = ad.metadata.contentType;
      if (typeof group[key] === 'undefined') {
        group[key] = [];
      }
      group[key].push(ad);
      return group;
    }, {});

    for (const [type, ads] of Object.entries(groups)) {
      if (type === METADATA_TYPE_AD_VAST) {
        if (includeImaAds && this._ima) {
          this._ima.load(ads);
        }
      } else {
        for (const ad of ads) {
          this._scheduleAd(ad);
        }
      }
    }
  }
}

Advertising.STATE_BEFORE_PREROLL = STATE_BEFORE_PREROLL;
Advertising.STATE_CONTENT_PLAYING = STATE_CONTENT_PLAYING;
Advertising.STATE_AD_PLAYING = STATE_AD_PLAYING;
